import { defineAsyncComponent, defineComponent, h, type Component } from "vue";

import { baseComponents, mosaicComponents, type MosaicComponentImporter } from "virtual:mosaic-components";

import type { ApplicationMosaic } from "@recordsure/unity-auth-machine";

const ErrorBoundary = defineComponent({
  name: "MosaicResolverErrorBoundary",
  inheritAttrs: false,
  setup() {
    return () =>
      h(
        "div",
        {
          role: "alert",
          title: "Error loading section. Please refresh your browser. If the problem persists, contact support.",
        },
        { default: () => h("i", { class: "fas fa-exclamation-triangle" }) },
      );
  },
});

export function resolveComponentLoader(name: string, activeStack: ApplicationMosaic[]) {
  for (const mosaic of activeStack) {
    const importer = mosaicComponents[mosaic.name]?.[name];
    if (importer != null) return importer;
  }

  const baseImporter = baseComponents[name];
  if (baseImporter != null) return baseImporter;

  throw new Error(`No compatible component is registered for "${name}"`);
}

const loaderCache = new WeakMap<MosaicComponentImporter, Component>();

export function resolveComponent(name: string, activeStack: ApplicationMosaic[]) {
  try {
    const loader = resolveComponentLoader(name, activeStack);
    if (loaderCache.has(loader)) return loaderCache.get(loader)!;

    const component = defineAsyncComponent({ loader, errorComponent: ErrorBoundary, delay: 200, timeout: 30_000 });
    loaderCache.set(loader, component);

    return component;
  } catch (e) {
    console.error(e);
    return ErrorBoundary;
  }
}
